import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Settings } from 'lucide-react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { usePageHeaderContext } from '../../../context/PageHeaderContext';
import ClickableImagePreview from '../../../ui-components/ClickableImagePreview';

import {
  useGetSystemSettingsQuery,
  useUpdateSystemSettingsMutation,
} from './systemSettingsApi';
import type { SystemSettingsRecord } from './model';

interface SystemSettingsFormValues {
  companyName: string;
  maxLoginAttempts: number;
  sessionTimeoutMinutes: number;
  copyrightText: string;
  androidAppVersion: string;
  androidForceUpdate: boolean;
  iosAppVersion: string;
  iosForceUpdate: boolean;
}

interface AssetPreviewProps {
  label: string;
  previewUrl: string | null;
  emptyLabel: string;
  imageClassName?: string;
}

const MAX_COMPANY_NAME_LENGTH = 150;
const MAX_COPYRIGHT_TEXT_LENGTH = 200;
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
const MAX_FAVICON_SIZE_BYTES = 1 * 1024 * 1024;

const EMPTY_FORM_VALUES: SystemSettingsFormValues = {
  companyName: '',
  maxLoginAttempts: 1,
  sessionTimeoutMinutes: 1,
  copyrightText: '',
  androidAppVersion: '',
  androidForceUpdate: false,
  iosAppVersion: '',
  iosForceUpdate: false,
};

const toFormValues = (
  settings?: Partial<SystemSettingsRecord>
): SystemSettingsFormValues => ({
  companyName: settings?.companyName ?? '',
  maxLoginAttempts: Number(settings?.maxLoginAttempts ?? 1),
  sessionTimeoutMinutes: Number(settings?.sessionTimeoutMinutes ?? 1),
  copyrightText: settings?.copyrightText ?? '',
  androidAppVersion: settings?.androidAppVersion ?? '',
  androidForceUpdate: Boolean(settings?.androidForceUpdate),
  iosAppVersion: settings?.iosAppVersion ?? '',
  iosForceUpdate: Boolean(settings?.iosForceUpdate),
});

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

const isFetchBaseQueryError = (error: unknown): error is FetchBaseQueryError =>
  typeof error === 'object' && error !== null && 'status' in error;

const AssetPreview: React.FC<AssetPreviewProps> = ({
  label,
  previewUrl,
  emptyLabel,
  imageClassName = 'h-10 w-auto max-w-full object-contain',
}) => {
  return (
    <div className="mt-2">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
        {label}
      </p>
      {previewUrl ? (
        <ClickableImagePreview
          src={previewUrl}
          alt={label}
          className={imageClassName}
        />
      ) : (
        <p className="text-xs text-neutral-400">{emptyLabel}</p>
      )}
    </div>
  );
};

const SystemSettingsPage: React.FC = () => {
  const { setSubtitle } = usePageHeaderContext();

  const [initialValues, setInitialValues] =
    useState<SystemSettingsFormValues>(EMPTY_FORM_VALUES);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [logoError, setLogoError] = useState('');
  const [faviconError, setFaviconError] = useState('');
  const [hasSavedLogo, setHasSavedLogo] = useState(false);
  const [hasSavedFavicon, setHasSavedFavicon] = useState(false);

  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const faviconInputRef = useRef<HTMLInputElement | null>(null);

  const { data, isFetching, isLoading, isError, error, refetch } =
    useGetSystemSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateSystemSettingsMutation();

  const isNotFoundError =
    isError && isFetchBaseQueryError(error) && error.status === 404;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<SystemSettingsFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  const companyNameValue = watch('companyName') ?? '';
  const copyrightTextValue = watch('copyrightText') ?? '';
  const androidAppVersionValue = watch('androidAppVersion') ?? '';
  const iosAppVersionValue = watch('iosAppVersion') ?? '';
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [faviconPreviewUrl, setFaviconPreviewUrl] = useState<string | null>(
    null
  );

  useEffect(() => {
    setSubtitle('Manage global branding and security configuration');
    return () => {
      setSubtitle(undefined);
    };
  }, [setSubtitle]);

  useEffect(() => {
    if (!data?.data) return;
    const normalized = toFormValues(data.data);
    setInitialValues(normalized);
    setHasSavedLogo(Boolean(data.data.logoUrl));
    setHasSavedFavicon(Boolean(data.data.faviconUrl));
    reset(normalized);
  }, [data, reset]);

  useEffect(() => {
    if (!isNotFoundError) return;
    setInitialValues(EMPTY_FORM_VALUES);
    setHasSavedLogo(false);
    setHasSavedFavicon(false);
    reset(EMPTY_FORM_VALUES);
  }, [isNotFoundError, reset]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [logoFile]);

  useEffect(() => {
    if (!faviconFile) {
      setFaviconPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(faviconFile);
    setFaviconPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [faviconFile]);

  const clearFiles = () => {
    setLogoFile(null);
    setFaviconFile(null);
    setLogoError('');
    setFaviconError('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    if (faviconInputRef.current) {
      faviconInputRef.current.value = '';
    }
  };

  const validateLogoFile = (file: File): string | null => {
    const validMimeTypes = ['image/png', 'image/jpeg'];
    const validExtensions = ['png', 'jpg', 'jpeg'];
    const extension = getFileExtension(file.name);
    const hasValidMime = validMimeTypes.includes(file.type);
    const hasValidExtension = validExtensions.includes(extension);

    if (!hasValidMime && !hasValidExtension) {
      return 'Logo must be PNG or JPG.';
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      return 'Logo size must be 2MB or less.';
    }
    return null;
  };

  const validateFaviconFile = (file: File): string | null => {
    const validMimeTypes = [
      'image/png',
      'image/x-icon',
      'image/vnd.microsoft.icon',
    ];
    const validExtensions = ['png', 'ico'];
    const extension = getFileExtension(file.name);
    const hasValidMime = validMimeTypes.includes(file.type);
    const hasValidExtension = validExtensions.includes(extension);

    if (!hasValidMime && !hasValidExtension) {
      return 'Favicon must be ICO or PNG.';
    }
    if (file.size > MAX_FAVICON_SIZE_BYTES) {
      return 'Favicon size must be 1MB or less.';
    }
    return null;
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateLogoFile(file);
    if (validationError) {
      setLogoFile(null);
      setLogoError(validationError);
      event.target.value = '';
      return;
    }

    setLogoError('');
    setLogoFile(file);
  };

  const handleFaviconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFaviconFile(file);
    if (validationError) {
      setFaviconFile(null);
      setFaviconError(validationError);
      event.target.value = '';
      return;
    }

    setFaviconError('');
    setFaviconFile(file);
  };

  const onSubmit: SubmitHandler<SystemSettingsFormValues> = async (values) => {
    if (logoError || faviconError) return;

    const formData = new FormData();
    formData.append('companyName', values.companyName.trim());
    formData.append('maxLoginAttempts', String(values.maxLoginAttempts));
    formData.append(
      'sessionTimeoutMinutes',
      String(values.sessionTimeoutMinutes)
    );
    formData.append('copyrightText', values.copyrightText.trim());
    formData.append('androidAppVersion', values.androidAppVersion.trim());
    formData.append(
      'androidForceUpdate',
      String(values.androidForceUpdate)
    );
    formData.append('iosAppVersion', values.iosAppVersion.trim());
    formData.append('iosForceUpdate', String(values.iosForceUpdate));

    if (logoFile) {
      formData.append('logoUrl', logoFile);
    }
    if (faviconFile) {
      formData.append('faviconUrl', faviconFile);
    }

    try {
      const response = await updateSettings(formData).unwrap();
      const normalized = toFormValues(response?.data);
      setInitialValues(normalized);
      setHasSavedLogo(Boolean(response?.data?.logoUrl));
      setHasSavedFavicon(Boolean(response?.data?.faviconUrl));
      reset(normalized);
      clearFiles();
    } catch {
      // Toast handling is centralized in baseQueryWithReauth.
    }
  };

  const handleCancel = () => {
    reset(initialValues);
    clearFiles();
  };

  const hasFileChanges = Boolean(logoFile || faviconFile);
  const logoDisplayUrl = logoPreviewUrl ?? data?.data?.logoUrl ?? null;
  const faviconDisplayUrl = faviconPreviewUrl ?? data?.data?.faviconUrl ?? null;
  const canSave = useMemo(
    () =>
      !isSaving &&
      !isLoading &&
      (isDirty || hasFileChanges) &&
      !logoError &&
      !faviconError,
    [isSaving, isLoading, isDirty, hasFileChanges, logoError, faviconError]
  );

  if (isFetching && !data && !isNotFoundError) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-brand-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {isError && !isNotFoundError && (
          <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
            <div className="flex items-center justify-between gap-4">
              <span>Unable to load system settings. Please try again.</span>
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-md border border-error-300 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-error-700 hover:bg-error-100"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-950">
          <div className="border-b border-neutral-200 px-6 py-5 dark:border-neutral-800">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-100 bg-brand-50 text-brand-600 dark:border-brand-900/30 dark:bg-brand-900/20">
                <Settings className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-neutral-900 dark:text-white">
                  Global Configuration
                </h2>
                <p className="mt-0.5 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                  Configure branding, security settings, and system assets.
                  Changes will apply system-wide immediately.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-8 px-6 py-6">
            <section className="space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                Branding
              </h3>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                  Company Name <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={MAX_COMPANY_NAME_LENGTH}
                  className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                    errors.companyName
                      ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                      : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                  }`}
                  placeholder="Enter company name"
                  {...register('companyName', {
                    required: 'Company name is required.',
                    maxLength: {
                      value: MAX_COMPANY_NAME_LENGTH,
                      message: `Company name cannot exceed ${MAX_COMPANY_NAME_LENGTH} characters.`,
                    },
                    validate: (value) =>
                      value.trim().length > 0 || 'Company name is required.',
                  })}
                />
                <div className="mt-1.5 flex items-center justify-between">
                  {errors.companyName ? (
                    <p className="text-xs font-medium text-error-500">
                      {errors.companyName.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-neutral-500">
                    {companyNameValue.length}/{MAX_COMPANY_NAME_LENGTH}{' '}
                    characters
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-neutral-200 pt-6 dark:border-neutral-800">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                Security
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                    Maximum Login Attempts{' '}
                    <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                      errors.maxLoginAttempts
                        ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                        : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                    }`}
                    {...register('maxLoginAttempts', {
                      required: 'Maximum login attempts is required.',
                      valueAsNumber: true,
                      validate: (value) => {
                        if (!Number.isFinite(value)) {
                          return 'Maximum login attempts is required.';
                        }
                        if (!Number.isInteger(value) || value < 1) {
                          return 'Maximum login attempts must be an integer greater than or equal to 1.';
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.maxLoginAttempts && (
                    <p className="mt-1.5 text-xs font-medium text-error-500">
                      {errors.maxLoginAttempts.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                    Session Timeout (minutes){' '}
                    <span className="text-error-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                      errors.sessionTimeoutMinutes
                        ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                        : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                    }`}
                    {...register('sessionTimeoutMinutes', {
                      required: 'Session timeout is required.',
                      valueAsNumber: true,
                      validate: (value) => {
                        if (!Number.isFinite(value)) {
                          return 'Session timeout is required.';
                        }
                        if (!Number.isInteger(value) || value < 1) {
                          return 'Session timeout must be an integer greater than or equal to 1.';
                        }
                        return true;
                      },
                    })}
                  />
                  {errors.sessionTimeoutMinutes && (
                    <p className="mt-1.5 text-xs font-medium text-error-500">
                      {errors.sessionTimeoutMinutes.message}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-neutral-200 pt-6 dark:border-neutral-800">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                Footer
              </h3>
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                  Copyright Text <span className="text-error-500">*</span>
                </label>
                <input
                  type="text"
                  maxLength={MAX_COPYRIGHT_TEXT_LENGTH}
                  className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                    errors.copyrightText
                      ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                      : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                  }`}
                  placeholder="Enter copyright text"
                  {...register('copyrightText', {
                    required: 'Copyright text is required.',
                    maxLength: {
                      value: MAX_COPYRIGHT_TEXT_LENGTH,
                      message: `Copyright text cannot exceed ${MAX_COPYRIGHT_TEXT_LENGTH} characters.`,
                    },
                    validate: (value) =>
                      value.trim().length > 0 || 'Copyright text is required.',
                  })}
                />
                <div className="mt-1.5 flex items-center justify-between">
                  {errors.copyrightText ? (
                    <p className="text-xs font-medium text-error-500">
                      {errors.copyrightText.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <p className="text-xs text-neutral-500">
                    {copyrightTextValue.length}/{MAX_COPYRIGHT_TEXT_LENGTH}{' '}
                    characters
                  </p>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-neutral-200 pt-6 dark:border-neutral-800">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                Mobile App Versions
              </h3>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                      Android App Version <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={32}
                      className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                        errors.androidAppVersion
                          ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                          : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                      }`}
                      placeholder="e.g. 1.0.0"
                      {...register('androidAppVersion', {
                        required: 'Android app version is required.',
                        maxLength: {
                          value: 32,
                          message:
                            'Android app version cannot exceed 32 characters.',
                        },
                        validate: (value) =>
                          value.trim().length > 0 ||
                          'Android app version is required.',
                      })}
                    />
                    <div className="mt-1.5 flex items-center justify-between">
                      {errors.androidAppVersion ? (
                        <p className="text-xs font-medium text-error-500">
                          {errors.androidAppVersion.message}
                        </p>
                      ) : (
                        <span />
                      )}
                      <p className="text-xs text-neutral-500">
                        {androidAppVersionValue.length}/32 characters
                      </p>
                    </div>
                  </div>

                  <label className="flex items-center justify-between rounded-lg border border-neutral-200 px-4 py-3 dark:border-neutral-800">
                    <div>
                      <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                        Force Android Update
                      </p>
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        Require Android users on older app versions to update.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
                      {...register('androidForceUpdate')}
                    />
                  </label>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                      iOS App Version <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      maxLength={32}
                      className={`w-full rounded-lg border px-3 py-2 text-sm text-neutral-900 outline-none transition focus:ring-4 dark:bg-neutral-900 dark:text-white ${
                        errors.iosAppVersion
                          ? 'border-error-500 focus:border-error-500 focus:ring-error-100 dark:focus:ring-error-900/30'
                          : 'border-neutral-200 focus:border-brand-500 focus:ring-brand-100 dark:border-neutral-700 dark:focus:ring-brand-900/30'
                      }`}
                      placeholder="e.g. 1.0.0"
                      {...register('iosAppVersion', {
                        required: 'iOS app version is required.',
                        maxLength: {
                          value: 32,
                          message:
                            'iOS app version cannot exceed 32 characters.',
                        },
                        validate: (value) =>
                          value.trim().length > 0 ||
                          'iOS app version is required.',
                      })}
                    />
                    <div className="mt-1.5 flex items-center justify-between">
                      {errors.iosAppVersion ? (
                        <p className="text-xs font-medium text-error-500">
                          {errors.iosAppVersion.message}
                        </p>
                      ) : (
                        <span />
                      )}
                      <p className="text-xs text-neutral-500">
                        {iosAppVersionValue.length}/32 characters
                      </p>
                    </div>
                  </div>

                  <label className="flex items-center justify-between rounded-lg border border-neutral-200 px-4 py-3 dark:border-neutral-800">
                    <div>
                      <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                        Force iOS Update
                      </p>
                      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                        Require iOS users on older app versions to update.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
                      {...register('iosForceUpdate')}
                    />
                  </label>
                </div>
              </div>
            </section>

            <section className="space-y-4 border-t border-neutral-200 pt-6 dark:border-neutral-800">
              <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400">
                Assets
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                    Logo Upload
                  </label>
                  <div className="flex min-h-10 items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      className="rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800"
                    >
                      Upload Logo
                    </button>
                    <span className="truncate text-xs text-neutral-500">
                      {logoFile?.name ??
                        (hasSavedLogo
                          ? 'Logo already configured'
                          : 'No file selected')}
                    </span>
                  </div>
                  {logoError && (
                    <p className="mt-1.5 text-xs font-medium text-error-500">
                      {logoError}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-neutral-500">
                    PNG or JPG • Max 2MB
                  </p>
                  <AssetPreview
                    label={logoFile ? 'Selected Logo Preview' : 'Current Logo Preview'}
                    previewUrl={logoDisplayUrl}
                    emptyLabel="No logo uploaded"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-neutral-700 dark:text-neutral-300">
                    Favicon Upload
                  </label>
                  <div className="flex min-h-10 items-center gap-2 rounded-lg border border-neutral-200 px-3 py-2 dark:border-neutral-700">
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept=".ico,.png,image/x-icon,image/vnd.microsoft.icon,image/png"
                      onChange={handleFaviconChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => faviconInputRef.current?.click()}
                      className="rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-800"
                    >
                      Upload Favicon
                    </button>
                    <span className="truncate text-xs text-neutral-500">
                      {faviconFile?.name ??
                        (hasSavedFavicon
                          ? 'Favicon already configured'
                          : 'No file selected')}
                    </span>
                  </div>
                  {faviconError && (
                    <p className="mt-1.5 text-xs font-medium text-error-500">
                      {faviconError}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-neutral-500">
                    ICO or PNG • Max 1MB
                  </p>
                  <AssetPreview
                    label={
                      faviconFile
                        ? 'Selected Favicon Preview'
                        : 'Current Favicon Preview'
                    }
                    previewUrl={faviconDisplayUrl}
                    emptyLabel="No favicon uploaded"
                    imageClassName="h-8 w-8 object-contain"
                  />
                </div>
              </div>
            </section>

            <div className="flex items-center gap-3 border-t border-neutral-200 pt-6 dark:border-neutral-800">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="inline-flex items-center justify-center rounded-lg border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSave}
                className="inline-flex items-center justify-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs font-medium text-neutral-700 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-300">
          Note: There is a single global settings record. Any changes will
          overwrite the previous configuration and apply system-wide immediately
          after saving.
        </div>
      </form>
    </div>
  );
};

export default SystemSettingsPage;
